import type { Provider } from '@ethersproject/providers'
import { waitForRpcQuietPeriod } from '@/services/rpc/throttledRpc'

/**
 * Public RPC endpoints commonly cap eth_getLogs block ranges (mainnet-rpc.lumia.org rejects
 * anything above 1,000,000 blocks with "block range too large"). Stay just under that most
 * common cap so a full-history scan works out of the box on such endpoints.
 */
const DEFAULT_CHUNK_SIZE = 900_000

const MAX_ATTEMPTS_PER_CHUNK = 4

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const isRateLimitError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error)
  return /\b429\b|\b50[234]\b|too many requests|rate limit|server unavailable|service unavailable|bad gateway/i.test(
    message,
  )
}

type ChunkQueryable<TFilter, TEvent> = {
  provider: Provider
  queryFilter(filter: TFilter, fromBlock?: number, toBlock?: number): Promise<TEvent[]>
}

/**
 * Merges a cached log list with the result of an incremental rescan that covered
 * [rescanFromBlock, head]: cached entries inside the rescanned range are replaced by the fresh
 * ones (so reorged-out events disappear), everything older is kept as is.
 */
export const mergeChunkedLogs = <TLog extends { blockNumber: number }>(
  cachedLogs: TLog[],
  freshLogs: TLog[],
  rescanFromBlock: number,
): TLog[] => {
  return cachedLogs.filter((log) => log.blockNumber < rescanFromBlock).concat(freshLogs)
}

export type ChunkedQueryOptions = {
  fromBlock?: number
  /** Last block to scan; defaults to the current chain head. */
  toBlock?: number
  chunkSize?: number
}

/**
 * Drop-in replacement for `contract.queryFilter(filter, 0, 'latest')` that splits the scan into
 * block ranges the RPC accepts. Chunks are fetched sequentially, oldest first, with a short pause
 * between requests and an exponential backoff retry on 429 responses, which keeps the request
 * rate acceptable for rate-limited public endpoints.
 */
export const queryFilterChunked = async <TFilter, TEvent>(
  contract: ChunkQueryable<TFilter, TEvent>,
  filter: TFilter,
  { fromBlock = 0, toBlock, chunkSize = DEFAULT_CHUNK_SIZE }: ChunkedQueryOptions = {},
): Promise<TEvent[]> => {
  const lastBlock = toBlock ?? (await contract.provider.getBlockNumber())

  const events: TEvent[] = []
  for (let start = fromBlock; start <= lastBlock; start += chunkSize) {
    const end = Math.min(start + chunkSize - 1, lastBlock)

    // The scan is background work: yield to interactive requests (safe info, transaction
    // details) instead of queueing dozens of chunks ahead of them in the shared dispatch budget.
    await waitForRpcQuietPeriod()

    for (let attempt = 1; ; attempt++) {
      try {
        events.push(...(await contract.queryFilter(filter, start, end)))
        break
      } catch (error) {
        if (attempt >= MAX_ATTEMPTS_PER_CHUNK || !isRateLimitError(error)) {
          throw error
        }
        await sleep(500 * 2 ** (attempt - 1) + Math.random() * 250)
      }
    }
  }

  return events
}
